/**
 * Player wrapper that abstracts native HLS (Safari) vs hls.js (everyone else).
 * Exposes a small imperative API the sync controller drives, and emits events
 * the controller listens to.
 */
import Hls from 'hls.js';

export type PlayerEvent = 'canplay' | 'stalled' | 'play' | 'pause' | 'seeked' | 'error';

type Listener = (...args: unknown[]) => void;

/**
 * The slice of the player API that the SyncController depends on. Extracted as
 * an interface so tests can substitute a fake player without instantiating
 * hls.js or a real <video> element.
 */
export interface PlayerLike {
  on(event: PlayerEvent, fn: Listener): () => void;
  loadSource(masterUrl: string): void;
  play(): Promise<void>;
  pause(): void;
  seekTo(seconds: number): void;
  getCurrentTime(): number;
  setPlaybackRate(rate: number): void;
  getPlaybackRate(): number;
}

export interface PlayerOptions {
  /**
   * Buffer ahead target (seconds). The plan calls for 60s since we have no
   * ABR fallback.
   */
  maxBufferLengthSeconds?: number;
  /** Hard cap on buffer bytes. */
  maxBufferSizeBytes?: number;
}

export class Player implements PlayerLike {
  private hls: Hls | null = null;
  private readonly listeners = new Map<PlayerEvent, Set<Listener>>();
  private currentSrc: string | null = null;
  private stalledTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public readonly video: HTMLVideoElement,
    private readonly opts: PlayerOptions = {},
  ) {
    video.addEventListener('canplay', () => this.emit('canplay'));
    video.addEventListener('play', () => this.emit('play'));
    video.addEventListener('pause', () => this.emit('pause'));
    video.addEventListener('seeked', () => this.emit('seeked'));
    video.addEventListener('error', () => this.emit('error', video.error));
    video.addEventListener('waiting', () => {
      // Debounce: don't fire `stalled` for transient seek-driven waiting,
      // initial buffer-fill after play(), or short network hiccups within the
      // pre-buffered window. A real stall warrants a room-wide pause; brief
      // waiting that self-recovers does not.
      if (this.stalledTimer != null) clearTimeout(this.stalledTimer);
      this.stalledTimer = setTimeout(() => this.emit('stalled'), 1500);
    });
    video.addEventListener('playing', () => {
      if (this.stalledTimer != null) clearTimeout(this.stalledTimer);
      this.stalledTimer = null;
    });
    video.addEventListener('seeking', () => {
      // Seeks cause a momentary `waiting` event even when seeking within the
      // buffered range. Clear any in-flight stall timer so a user-initiated
      // (or drift-correction) seek can't be misinterpreted as a stall.
      if (this.stalledTimer != null) clearTimeout(this.stalledTimer);
      this.stalledTimer = null;
    });
  }

  loadSource(masterUrl: string): void {
    if (this.currentSrc === masterUrl) return;
    this.currentSrc = masterUrl;
    if (this.canUseNativeHls()) {
      this.video.src = masterUrl;
      return;
    }
    if (this.hls) this.hls.destroy();
    this.hls = new Hls({
      capLevelToPlayerSize: false,
      maxBufferLength: this.opts.maxBufferLengthSeconds ?? 60,
      maxBufferSize: this.opts.maxBufferSizeBytes ?? 60 * 1024 * 1024,
      enableWorker: true,
      lowLatencyMode: false,
    });
    this.hls.on(Hls.Events.ERROR, (_e, data) => {
      if (data.fatal) this.emit('error', data);
    });
    this.hls.loadSource(masterUrl);
    this.hls.attachMedia(this.video);
  }

  destroy(): void {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    this.listeners.clear();
  }

  on(event: PlayerEvent, fn: Listener): () => void {
    let s = this.listeners.get(event);
    if (!s) {
      s = new Set();
      this.listeners.set(event, s);
    }
    s.add(fn);
    return () => s?.delete(fn);
  }

  private emit(event: PlayerEvent, ...args: unknown[]): void {
    const s = this.listeners.get(event);
    if (!s) return;
    for (const fn of s) {
      try {
        fn(...args);
      } catch (e) {
        console.error('player listener threw', e);
      }
    }
  }

  // ─── Imperative controls ────────────────────────────────────────────────
  async play(): Promise<void> {
    try {
      await this.video.play();
    } catch (e) {
      // Autoplay can be blocked; surface as a stalled event so the UI prompts.
      this.emit('error', e);
    }
  }
  pause(): void {
    this.video.pause();
  }
  seekTo(seconds: number): void {
    this.video.currentTime = Math.max(0, seconds);
  }
  getCurrentTime(): number {
    return this.video.currentTime;
  }
  setPlaybackRate(rate: number): void {
    if (!(rate > 0)) return;
    // Avoid redundant writes — setting playbackRate (even to the same value)
    // can restart the audio time-stretch algorithm in some browsers, causing
    // audible glitches.
    if (Math.abs(this.video.playbackRate - rate) < 0.001) return;
    this.video.playbackRate = rate;
  }
  getPlaybackRate(): number {
    return this.video.playbackRate;
  }
  getBufferedAheadSeconds(): number {
    const t = this.video.currentTime;
    const ranges = this.video.buffered;
    for (let i = 0; i < ranges.length; i++) {
      if (ranges.start(i) <= t && t <= ranges.end(i)) return ranges.end(i) - t;
    }
    return 0;
  }
  isPaused(): boolean {
    return this.video.paused;
  }

  private canUseNativeHls(): boolean {
    return (
      this.video.canPlayType('application/vnd.apple.mpegurl') !== '' &&
      // hls.js is preferred on Chrome/Firefox even if a future polyfill ever lights this up.
      /Safari/.test(navigator.userAgent) &&
      !/Chrome/.test(navigator.userAgent)
    );
  }
}
